import { FC, useMemo } from "react";
import { Paper, Skeleton, Typography } from "@mui/material";
import useBasePath from "src/utils/hooks/useBasePath";
import __ from "src/utils/Translations";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesCompanyConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchCompanyConfigurations } from "src/Pages/Company/Absences/api/companyConfiguration.queries";
import { transformCompanyConfigurationToFormValues } from "src/utils/Company/Absences/parseCompanyConfiguration";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import Title from "src/Pages/Company/Absences/Title/Title";
import PermissionsAlert from "src/Pages/Company/Absences/PermissionsAlert";
import AbsencesConfigurationForm from "./AbsencesConfigurationForm";

const AbsencesConfiguration: FC = () => {
  const basePath = useBasePath();
  const companyId = useCompanyId();
  const logStatus = useLogStatus();
  const canWriteCompanyConfiguration = usePermission(
    absencesCompanyConfigurationWrite
  );

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsencesConfiguration.tsx",
    });
  }

  const {
    data: companyConfigurationData,
    isFetching,
    isError,
  } = useFetchCompanyConfigurations(companyId);

  const defaultValues = useMemo(
    () => transformCompanyConfigurationToFormValues(companyConfigurationData),
    [companyConfigurationData]
  );

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Ustawienia modułu Nieobecności"),
          },
        ]}
      />
      <Title title={__("Ustawienia modułu Nieobecności")} />
      {!canWriteCompanyConfiguration && <PermissionsAlert />}
      <Paper sx={{ p: "2rem" }} data-testid="absences-configuration">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isError ? (
          <ErrorDataFetching />
        ) : isFetching ? (
          <Skeleton height="2rem" width="50%" sx={{ m: "1rem 0" }} />
        ) : (
          <AbsencesConfigurationForm defaultValues={defaultValues} />
        )}
      </Paper>
    </>
  );
};

export default AbsencesConfiguration;
